import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors3 } from "../../colors/color";
import Printer2 from "../../components/Printer2";
import GraphLoader from "../../components/GraphLoader";
import { Grid, Button, ButtonGroup, Chip } from "@mui/material";
import { forecastingModuleDegradationAction } from "../../actions/predictiveAction";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const PredictiveModuleDegradation = ({ selectedLebel, databaseStr }) => {
  const {
    forecastedModuleDegradation,
    moduleDegradationLoading: loading,
  } = useSelector((state) => state.forecasting);

  const graphRef = useRef(null);

  // State for legend visibility
  const [legendItems, setLegendItems] = useState({
    "DC Side Performance Degradation": true,
    "Forecasted DC Side Performance Degradation": true,
  });

  // State for time period selection
  const [selectedPeriod, setSelectedPeriod] = useState("historical");
  const [selectedFilter, setSelectedFilter] = useState("all");

  // Get the current date range type from selectedLebel
  const currentDateRangeType = selectedLebel?.dateRangeType || "month";

  // Legend colors mapping
  const legendColors = {
    "DC Side Performance Degradation": colors3[0],
    "Forecasted DC Side Performance Degradation": "#203864",
  };

  // Handle legend click
  const handleLegendClick = (legendName) => {
    setLegendItems(prev => ({
      ...prev,
      [legendName]: !prev[legendName]
    }));
  };

  // Dynamic time period options based on dateRangeType
  const getTimePeriods = () => {
    switch (currentDateRangeType) {
      case "day":
        return [
          { label: "Historical", value: "historical", description: "View past data (daily)" },
          { label: "Next 7 Days", value: "7days", description: "Forecast next 7 days" },
          { label: "Next 30 Days", value: "30days", description: "Forecast next 30 days" },
        ];
      case "month":
        return [
          { label: "Historical", value: "historical", description: "View past data (monthly)" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
      case "year":
        return [
          { label: "Historical", value: "historical", description: "View past data (yearly)" },
          { label: "Next 2 Years", value: "2years", description: "Forecast next 2 years" },
          { label: "Next 5 Years", value: "5years", description: "Forecast next 5 years" },
        ];
      default:
        return [
          { label: "Historical", value: "historical", description: "View past data" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
    }
  };

  const timePeriods = getTimePeriods();

  // Filter options configuration
  const filterOptions = [
    { value: "all", label: "Show All" },
    { value: "gt_5", label: "DC Side Performance Degradation > 5%" },
    { value: "gt_25", label: "DC Side Performance Degradation > 25%" },
  ];

  // Helper function to get slice limit based on period and date range type
  const getSliceLimit = (period) => {
    switch (period) {
      case "7days": return 7;
      case "30days": return 30;
      case "6months": return 6;
      case "year": return 12;
      case "2years": return 2;
      case "5years": return 5;
      default: return null;
    }
  };

  // Helper function to get forecast value based on selected period
  const getForecastValue = (period) => {
    switch (period) {
      case "historical": return "historical";
      case "7days": return "7days";
      case "30days": return "30days";
      case "6months": return "6months";
      case "year": return "year";
      case "2years": return "2years";
      case "5years": return "5years";
      default: return "historical";
    }
  };

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof num !== 'number' || isNaN(num)) {
      return null;
    }
    return num.toFixed(2);
  };

  // Custom bar component that shows blank space for null values but maintains bar width
  const CustomBar = (props) => {
    const { fill, x, y, width, height, value } = props;

    // If value is null or undefined, render an empty transparent bar (maintains space)
    if (value === null || value === undefined || isNaN(value)) {
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="transparent"
          stroke="none"
        />
      );
    }

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        rx={2}
        ry={2}
      />
    );
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const periodOverPeriodChange = data["Period Over Period Change"];
      const periodOverPeriodPercentage = data["Period Over Period Percentage"];

      const periodLabel = currentDateRangeType === "5_min" ? "5 Minutes" : currentDateRangeType === "day" ? "Day" :
        currentDateRangeType === "year" ? "Year" : "Month";

      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            minWidth: "220px",
          }}
        >
          <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>
            {label}
          </p>
          {payload.map((entry, index) => {
            const hasNoData = entry.value === null || entry.value === undefined || entry.value === "N/A";
            return (
              <p
                key={index}
                style={{
                  margin: "2px 0",
                  color: entry.color,
                  fontSize: "0.8rem",
                  fontStyle: hasNoData ? "italic" : "normal",
                }}
              >
                {entry.name}: <strong>{hasNoData ? "No Data" : `${formatNumber(entry.value)}%`}</strong>
              </p>
            );
          })}
          {periodOverPeriodChange !== null && periodOverPeriodPercentage !== null && (
            <p
              style={{
                margin: "5px 0 0 0",
                padding: "3px 6px",
                borderRadius: "3px",
                // backgroundColor: periodOverPeriodChange >= 0 ? "#f6ffed" : "#fff2f0",
                // border: `1px solid ${periodOverPeriodChange >= 0 ? "#b7eb8f" : "#ffccc7"}`,
                backgroundColor: periodOverPeriodChange >= 0 ? "#fff2f0" : "#f6ffed",
                border: `1px solid ${periodOverPeriodChange >= 0 ? "#ffccc7" : "#b7eb8f"}`,
                fontSize: "0.75rem",
                fontWeight: "bold",
                color: periodOverPeriodChange >= 0 ? "#a8071a" : "#389e0d",
                // color: periodOverPeriodChange >= 0 ? "#389e0d" : "#a8071a",
              }}
            >
              vs Previous {periodLabel}:
              <span style={{ marginLeft: "5px" }}>
                {periodOverPeriodChange >= 0 ? "+" : ""}{formatNumber(periodOverPeriodChange)}%
                ({periodOverPeriodChange >= 0 ? "+" : ""}{formatNumber(periodOverPeriodPercentage)}%)
              </span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const minMaxRangeAndGraphData = useMemo(() => {
    if (forecastedModuleDegradation && forecastedModuleDegradation?.length > 0) {
      // Filter data based on selected time period
      let filteredData = [...forecastedModuleDegradation];

      if (selectedPeriod !== "historical") {
        const sliceLimit = getSliceLimit(selectedPeriod);
        if (sliceLimit && filteredData.length > sliceLimit) {
          filteredData = filteredData.slice(0, sliceLimit);
        }
      }

      // Create the base graph data with difference calculation
      const baseGraphData = filteredData?.reduce((acc, curr, index, array) => {
        let periodOverPeriodChange = null;
        let periodOverPeriodPercentage = null;

        // Calculate difference from previous period
        if (index > 0) {
          const currentDegradation = curr["Module Degradation"] || curr["Forecasted Module Degradation"];
          const prevDegradation = array[index - 1]["Module Degradation"] || array[index - 1]["Forecasted Module Degradation"];

          if (currentDegradation != null && prevDegradation != null) {
            const currentValue = typeof currentDegradation === 'string'
              ? parseFloat(currentDegradation)
              : currentDegradation;
            const prevValue = typeof prevDegradation === 'string'
              ? parseFloat(prevDegradation)
              : prevDegradation;

            periodOverPeriodChange = currentValue - prevValue;
            periodOverPeriodPercentage = prevValue !== 0
              ? (periodOverPeriodChange / prevValue) * 100
              : 0;
          }
        }

        const obj = { Date: curr["Date"] };

        // Ensure numeric values (keep null as null)
        obj["DC Side Performance Degradation"] = curr["Module Degradation"] != null && !isNaN(curr["Module Degradation"])
          ? (typeof curr["Module Degradation"] === 'string' ? parseFloat(curr["Module Degradation"]) : curr["Module Degradation"])
          : null;

        obj["Forecasted DC Side Performance Degradation"] = curr["Forecasted Module Degradation"] != null && !isNaN(curr["Forecasted Module Degradation"])
          ? (typeof curr["Forecasted Module Degradation"] === 'string' ? parseFloat(curr["Forecasted Module Degradation"]) : curr["Forecasted Module Degradation"])
          : null;

        obj["Period Over Period Change"] = periodOverPeriodChange !== null ? Number(periodOverPeriodChange.toFixed(2)) : null;
        obj["Period Over Period Percentage"] = periodOverPeriodPercentage !== null ? Number(periodOverPeriodPercentage.toFixed(2)) : null;

        acc.push(obj);
        return acc;
      }, []) || [];

      // Apply filter based on selection
      const filteredGraphData = baseGraphData.filter((item) => {
        const moduleDegradation = item["DC Side Performance Degradation"];
        const forecastedModuleDegradation = item["Forecasted DC Side Performance Degradation"];

        switch (selectedFilter) {
          case "gt_5":
            return (moduleDegradation !== null && moduleDegradation > 5) ||
              (forecastedModuleDegradation !== null && forecastedModuleDegradation > 5);
          case "gt_25":
            return (moduleDegradation !== null && moduleDegradation > 25) ||
              (forecastedModuleDegradation !== null && forecastedModuleDegradation > 25);
          case "all":
          default:
            return true;
        }
      });

      // Calculate min/max based on filtered data
      const allValues = [];
      filteredGraphData.forEach(item => {
        if (item["DC Side Performance Degradation"] != null && !isNaN(item["DC Side Performance Degradation"])) allValues.push(item["DC Side Performance Degradation"]);
        if (item["Forecasted DC Side Performance Degradation"] != null && !isNaN(item["Forecasted DC Side Performance Degradation"])) allValues.push(item["Forecasted DC Side Performance Degradation"]);
      });

      const unitLabel = currentDateRangeType === "day" ? "days" :
        currentDateRangeType === "year" ? "years" : "months";

      const hasModuleDegradation = filteredGraphData.some(item => item["DC Side Performance Degradation"] != null && !isNaN(item["DC Side Performance Degradation"]));
      const hasForecastedModuleDegradation = filteredGraphData.some(item => item["Forecasted DC Side Performance Degradation"] != null && !isNaN(item["Forecasted DC Side Performance Degradation"]));

      return {
        graphData: filteredGraphData,
        min: allValues.length > 0 ? Math.floor(Math.min(...allValues)) : 0,
        max: allValues.length > 0 ? Math.ceil(Math.max(...allValues)) : 100,
        totalCount: baseGraphData.length,
        filteredCount: filteredGraphData.length,
        unitLabel: unitLabel,
        hasModuleDegradation: hasModuleDegradation,
        hasForecastedModuleDegradation: hasForecastedModuleDegradation
      };
    }
    return { graphData: [], min: 0, max: 100, totalCount: 0, filteredCount: 0, unitLabel: "months" };
  }, [forecastedModuleDegradation, selectedPeriod, selectedFilter, currentDateRangeType]);

  // Custom label formatter for bar labels
  const renderBarLabel = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "";
    return formatNumber(value);
  };

  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!selectedPeriod || !selectedLebel?.dateRangeType) return;

      const timeRange = getForecastValue(selectedPeriod);
      dispatch(
        forecastingModuleDegradationAction(
          { ...selectedLebel, forecast: timeRange },
          databaseStr
        )
      );
    }, 200);

    return () => clearTimeout(timer);
  }, [selectedPeriod, selectedLebel?.dateRangeType, databaseStr, dispatch, selectedLebel]);

  // Reset selected period when date range type changes
  useEffect(() => {
    setSelectedPeriod("historical");
  }, [currentDateRangeType]);

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setSelectedFilter(e.target.value);
  };

  // Get display unit for the title
  const getDisplayUnit = () => {
    switch (currentDateRangeType) {
      case "day": return "Daily";
      case "month": return "Monthly";
      case "year": return "Yearly";
      default: return "Monthly";
    }
  };

  return (
    <>
      <Grid container xl={12} textAlign={"center"}>
        <Grid container xl={12}>
          <Grid
            item
            xl={11.8}
            lg={11.8}
            md={11.8}
            sm={11.8}
            xs={11.8}
            style={{
              background: "linear-gradient(to bottom, rgb(3, 99, 125),rgb(4, 128, 162),rgb(4, 135, 172))",
              margin: "0 auto 0.5vmax",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.5vmax",
            }}
          >
            <Grid
              item
              xs={12}
              style={{
                background: "white",
                padding: "0.5vmax 0.5vmax",
                position: "relative",
                overflow: "auto",
              }}
            >
              {/* Fixed Header - Sticky */}
              <div
                style={{
                  padding: "1rem 1.5rem",
                  position: "sticky",
                  left: "0",
                  right: "0",
                  top: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "center",
                  zIndex: "10",
                  backgroundColor: "white",
                  borderBottom: "1px solid #f0f0f0",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                {/* Left side - Time Period Selection */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
                  <label style={{ fontSize: "0.9rem", fontWeight: 500, color: "#333" }}>
                    Time Period ({getDisplayUnit()}):
                  </label>
                  <ButtonGroup variant="outlined" size="small" style={{ display: "flex", flexWrap: "wrap" }}>
                    {timePeriods.map((period) => (
                      <Button
                        key={period.value}
                        onClick={() => handlePeriodChange(period.value)}
                        variant={selectedPeriod === period.value ? "contained" : "outlined"}
                        style={{
                          backgroundColor: selectedPeriod === period.value ? "#1890ff" : "transparent",
                          color: selectedPeriod === period.value ? "white" : "#666",
                          borderColor: "#d0d0d0",
                          fontSize: "0.8rem",
                          padding: "4px 12px",
                          minWidth: "90px",
                        }}
                        title={period.description}
                      >
                        {period.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>

                {/* Center - Title */}
                <div style={{ position: "absolute", left: 0, right: 0, flex: 1, textAlign: "center" }}>
                  <h5 style={{ margin: "0 0 5px 0" }}>
                    {selectedPeriod === "historical" ? "Historical" : "Forecasted"} DC Side Performance Degradation (%) - {getDisplayUnit()} Data
                    {selectedFilter !== "all" && (
                      <Chip
                        label="Filtered"
                        size="small"
                        style={{
                          marginLeft: "8px",
                          backgroundColor: "#fff2e8",
                          color: "#fa541c",
                          borderColor: "#ffbb96",
                          fontSize: "0.7rem",
                          height: "20px",
                        }}
                        variant="outlined"
                      />
                    )}
                  </h5>
                </div>

                {/* Right side - Filter and Printer */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem", justifyContent: "center", zIndex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <label htmlFor="module-degradation-filter" style={{ fontSize: "0.85rem", fontWeight: 500, color: "#333" }}>
                      Filter:
                    </label>
                    <select
                      id="module-degradation-filter"
                      value={selectedFilter}
                      onChange={handleFilterChange}
                      style={{
                        padding: "0.4rem 0.8rem",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        backgroundColor: "white",
                        cursor: "pointer",
                        minWidth: "180px",
                      }}
                    >
                      {filterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {selectedFilter !== "all" && minMaxRangeAndGraphData.totalCount > 0 && (
                      <Chip
                        label={`${minMaxRangeAndGraphData.filteredCount}/${minMaxRangeAndGraphData.totalCount} ${minMaxRangeAndGraphData.unitLabel}`}
                        size="small"
                        style={{
                          fontSize: "0.7rem",
                          backgroundColor: "#e6f7ff",
                          color: "#1890ff",
                          borderColor: "#91d5ff",
                          height: "22px",
                        }}
                        variant="outlined"
                      />
                    )}
                  </div>
                  <Printer2
                    dataSetDetail={{
                      data: minMaxRangeAndGraphData.graphData,
                      name: `Forecasted DC Side Performance Degradation - ${getDisplayUnit()} (${selectedPeriod !== "historical" ? "Forecasted" : "Historical"})`,
                      ref: graphRef,
                      refCurrent: graphRef.current?.container,
                    }}
                  />
                </div>
              </div>

              {/* Custom Clickable Legend Container - Sticky */}
              <div
                style={{
                  position: "sticky",
                  left: "0",
                  top: "85px",
                  zIndex: "1",
                  backgroundColor: "rgba(255, 255, 255, 0.98)",
                  padding: "12px 20px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "40px",
                  flexWrap: "wrap",
                }}
              >
                {Object.keys(legendItems).map((legendName) => {
                  const hasData = legendName === "DC Side Performance Degradation"
                    ? minMaxRangeAndGraphData.hasModuleDegradation
                    : minMaxRangeAndGraphData.hasForecastedModuleDegradation;

                  return !hasData ? null : (
                    <div
                      key={legendName}
                      onClick={() => handleLegendClick(legendName)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        userSelect: "none",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        backgroundColor: legendItems[legendName] ? "transparent" : "#f5f5f5",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: legendColors[legendName],
                          borderRadius: "3px",
                          opacity: legendItems[legendName] ? 1 : 0.4,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          color: legendItems[legendName] ? "#333" : "#999",
                          textDecoration: legendItems[legendName] ? "none" : "line-through",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {legendName}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Graph Container */}
              <div
                style={{
                  minWidth: "100%",
                  width: minMaxRangeAndGraphData.graphData?.length > 0
                    ? 2.5 * minMaxRangeAndGraphData.graphData.length * 55 + "px"
                    : "100%",

                }}
                className="forecasting-module-degradation-container"
              >
                <ResponsiveContainer width="100%" height={"100%"}>
                  {loading ? (
                    <div style={{ position: "absolute", top: "0", bottom: "0", right: "0", left: "0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <GraphLoader content={`${selectedPeriod !== "historical" ? "Forecasting" : "Loading"} DC Side Performance Degradation`} />
                    </div>
                  ) : minMaxRangeAndGraphData.graphData?.length > 0 ? (
                    <ComposedChart
                      barCategoryGap={"10%"}
                      barGap={5}
                      ref={graphRef}
                      margin={{ top: 15, bottom: 5, left: 5, right: 5 }}
                      data={minMaxRangeAndGraphData.graphData || []}
                    >
                      <XAxis
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        dataKey="Date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={5}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                      />
                      <CartesianGrid stroke="grey" strokeWidth={0.3} />

                      <YAxis

                        type="number"
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        tickLine={false}
                        tickMargin={5}
                        tickCount={6}
                        domain={[minMaxRangeAndGraphData.min, minMaxRangeAndGraphData.max]}
                        label={{
                          value: "DC Side Performance Degradation (%)",
                          angle: -90,
                          position: "insideLeft",
                          dy: 100,
                          style: { fontSize: "0.7rem", fontWeight: "bold" },
                        }}
                      />

                      <Tooltip cursor={{ fill: "none" }} content={<CustomTooltip />} contentStyle={{ fontSize: "0.7rem" }} />

                      {/* DC Side Performance Degradation Bar */}
                      {minMaxRangeAndGraphData.hasModuleDegradation && (
                        <Bar
                          hide={!legendItems["DC Side Performance Degradation"]}
                          dataKey="DC Side Performance Degradation"
                          fill={legendColors["DC Side Performance Degradation"]}
                          barSize={35}
                          shape={<CustomBar />}
                        >
                          <LabelList
                            dataKey="DC Side Performance Degradation"
                            position="top"
                            fontSize={"0.8rem"}
                            fontWeight={600}
                            formatter={renderBarLabel}
                          />
                        </Bar>
                      )}

                      {/* Forecasted DC Side Performance Degradation Bar */}
                      {minMaxRangeAndGraphData.hasForecastedModuleDegradation && (
                        <Bar
                          hide={!legendItems["Forecasted DC Side Performance Degradation"]}
                          dataKey="Forecasted DC Side Performance Degradation"
                          fill={legendColors["Forecasted DC Side Performance Degradation"]}
                          barSize={35}
                          shape={<CustomBar />}
                        >
                          <LabelList
                            dataKey="Forecasted DC Side Performance Degradation"
                            position="top"
                            fontSize={"0.8rem"}
                            fontWeight={600}
                            formatter={renderBarLabel}
                          />
                        </Bar>
                      )}
                    </ComposedChart>
                  ) : (
                    <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "1rem" }}>
                      <h3>
                        {selectedFilter !== "all"
                          ? "No data matches the filter criteria"
                          : "No data available for the selected period"}
                      </h3>
                      {selectedFilter !== "all" && (
                        <button
                          onClick={() => setSelectedFilter("all")}
                          style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "#1890ff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          Show All Data
                        </button>
                      )}
                    </div>
                  )}
                </ResponsiveContainer>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default PredictiveModuleDegradation;